import { useEffect, useState } from "react";
import { DollarSign, Package, Users, ShoppingBag, TrendingUp, AlertTriangle } from "lucide-react";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, BarChart, Bar, PieChart, Pie, Cell, Legend } from "recharts";
import api, { formatPrice } from "../../lib/api";

const COLORS = ["#D4AF37", "#E63946", "#A88728", "#F0C74A"];

export default function AdminDashboard() {
    const [data, setData] = useState(null);

    useEffect(() => {
        api.get("/admin/analytics").then(({ data }) => setData(data));
    }, []);

    if (!data) {
        return <div className="text-gold">Loading…</div>;
    }

    const stats = [
        { label: "Revenue", value: formatPrice(data.total_revenue), icon: DollarSign, color: "text-gold" },
        { label: "Orders", value: data.total_orders, icon: ShoppingBag, color: "text-white" },
        { label: "Customers", value: data.total_customers, icon: Users, color: "text-white" },
        { label: "Products", value: data.total_products, icon: Package, color: "text-white" },
    ];

    return (
        <div data-testid="admin-dashboard">
            <p className="text-[11px] uppercase tracking-[0.4em] text-gold">Atelier</p>
            <h1 className="mt-2 font-display text-4xl text-white">Overview</h1>

            <div className="mt-8 grid grid-cols-2 gap-4 lg:grid-cols-4">
                {stats.map((s) => (
                    <div key={s.label} className="border border-border-dim bg-surface p-6" data-testid={`stat-${s.label.toLowerCase()}`}>
                        <div className="flex items-center justify-between">
                            <p className="text-[10px] uppercase tracking-[0.25em] text-white/50">{s.label}</p>
                            <s.icon className={`h-4 w-4 ${s.color}`} />
                        </div>
                        <p className="mt-4 font-display text-3xl text-white">{s.value}</p>
                    </div>
                ))}
            </div>

            <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-3">
                <div className="border border-border-dim bg-surface p-6 lg:col-span-2">
                    <div className="flex items-center justify-between">
                        <h2 className="font-display text-2xl text-white">Revenue</h2>
                        <TrendingUp className="h-4 w-4 text-gold" />
                    </div>
                    <p className="text-xs text-white/40">Last 14 days · paid orders</p>
                    <div className="mt-6 h-64">
                        {data.revenue_series.length === 0 ? (
                            <div className="grid h-full place-items-center text-white/40">No paid orders yet</div>
                        ) : (
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={data.revenue_series}>
                                    <XAxis dataKey="date" stroke="#666" fontSize={10} />
                                    <YAxis stroke="#666" fontSize={10} />
                                    <Tooltip
                                        contentStyle={{
                                            background: "#141414",
                                            border: "1px solid #2A2A2A",
                                            color: "#fff",
                                        }}
                                    />
                                    <Line type="monotone" dataKey="revenue" stroke="#D4AF37" strokeWidth={2} dot={{ fill: "#D4AF37" }} />
                                </LineChart>
                            </ResponsiveContainer>
                        )}
                    </div>
                </div>

                <div className="border border-border-dim bg-surface p-6">
                    <h2 className="font-display text-2xl text-white">By category</h2>
                    <p className="text-xs text-white/40">Revenue split</p>
                    <div className="mt-6 h-64">
                        {data.categories.length === 0 ? (
                            <div className="grid h-full place-items-center text-white/40">No data</div>
                        ) : (
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie data={data.categories} dataKey="revenue" nameKey="category" innerRadius={50} outerRadius={90} paddingAngle={2}>
                                        {data.categories.map((_, i) => (
                                            <Cell key={i} fill={COLORS[i % COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Legend wrapperStyle={{ color: "#fff", fontSize: 10 }} />
                                    <Tooltip contentStyle={{ background: "#141414", border: "1px solid #2A2A2A", color: "#fff" }} />
                                </PieChart>
                            </ResponsiveContainer>
                        )}
                    </div>
                </div>
            </div>

            <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
                <div className="border border-border-dim bg-surface p-6">
                    <h2 className="font-display text-2xl text-white">Top pieces</h2>
                    <div className="mt-6 h-64">
                        {data.top_products.length === 0 ? (
                            <div className="grid h-full place-items-center text-white/40">No sales yet</div>
                        ) : (
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={data.top_products}>
                                    <XAxis dataKey="name" stroke="#666" fontSize={9} interval={0} angle={-15} height={50} />
                                    <YAxis stroke="#666" fontSize={10} />
                                    <Tooltip contentStyle={{ background: "#141414", border: "1px solid #2A2A2A", color: "#fff" }} />
                                    <Bar dataKey="revenue" fill="#D4AF37" />
                                </BarChart>
                            </ResponsiveContainer>
                        )}
                    </div>
                </div>

                <div className="border border-border-dim bg-surface p-6">
                    <div className="flex items-center gap-3">
                        <AlertTriangle className="h-5 w-5 text-heart" />
                        <h2 className="font-display text-2xl text-white">Low stock</h2>
                    </div>
                    <div className="mt-6 space-y-3">
                        {data.low_stock.length === 0 ? (
                            <p className="text-white/40">All stock levels healthy.</p>
                        ) : (
                            data.low_stock.map((p) => (
                                <div key={p.id} className="flex items-center gap-3 border-b border-border-dim pb-3 last:border-b-0" data-testid={`low-stock-${p.id}`}>
                                    <img src={p.images?.[0]} alt="" className="h-12 w-10 object-cover" />
                                    <p className="flex-1 text-sm text-white">{p.name}</p>
                                    <span className="border border-heart px-2 py-1 text-[10px] uppercase tracking-widest text-heart">
                                        {p.stock} left
                                    </span>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
